import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

// Get Theme routes if available
let Theme = {};
if (typeof window !== 'undefined' && window.Theme) {
  Theme = window.Theme;
}

class RecentlyViewedProducts extends HTMLElement {
  /**
   * The intersection observer for lazy loading
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadProducts();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    // Prevent section from being hidden
    this.style.display = 'block';
    
    // Check if element is already visible, if so load immediately
    const rect = this.getBoundingClientRect();
    const isVisible = rect.top < window.innerHeight + 400;
    
    if (isVisible) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        this.#loadProducts();
      }, 100);
    } else {
      this.#intersectionObserver.observe(this);
    }
    
    // Watch for any attempts to hide the section after products are loaded
    this.#watchForVisibilityChanges();
  }
  
  /**
   * Watch for visibility changes and prevent hiding when products are loaded
   */
  #watchForVisibilityChanges() {
    const observer = new MutationObserver((mutations) => {
      if (this.dataset.hasProducts === 'true') {
        // If products are loaded, ensure section stays visible
        const computedStyle = window.getComputedStyle(this);
        if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden') {
          this.style.display = 'block';
          this.style.visibility = 'visible';
        }
      }
    });
    
    observer.observe(this, {
      attributes: true,
      attributeFilter: ['style', 'class'],
      childList: false,
      subtree: false
    });
    
    // Store observer for cleanup
    this.#visibilityObserver = observer;
  }
  
  #visibilityObserver = null;

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
    if (this.#visibilityObserver) {
      this.#visibilityObserver.disconnect();
    }
  }

  /**
   * Load the recently viewed products
   */
  async #loadProducts() {
    const { sectionId, currentProductId, maxProducts } = this.dataset;
    
    if (!sectionId) {
      console.error('Section ID is required');
      return;
    }

    // Check if RecentlyViewed is available
    if (!RecentlyViewed || typeof RecentlyViewed.getProducts !== 'function') {
      console.error('RecentlyViewed class not available');
      this.style.display = 'none';
      return;
    }

    // Get recently viewed product IDs from localStorage
    const viewedProductIds = RecentlyViewed.getProducts();
    
    // Check localStorage directly
    const stored = localStorage.getItem('viewedProducts');
    
    // Filter out the current product if it's in the list
    const filteredIds = viewedProductIds.filter(
      (id) => id !== currentProductId
    );

    // Limit to max products (default to 7)
    const productIds = filteredIds.slice(0, parseInt(maxProducts || '7', 10));

    if (productIds.length === 0) {
      console.warn('No products to display - localStorage might be empty or current product is the only one viewed');
      this.style.display = 'none';
      return;
    }

    try {
      // Build URL with search parameters for section rendering API
      // Use /search endpoint directly to ensure search.results is populated
      const searchBaseUrl = (typeof window.Theme !== 'undefined' && window.Theme.routes && window.Theme.routes.search_url) 
        ? window.Theme.routes.search_url 
        : '/search';
      const url = new URL(searchBaseUrl, window.location.origin);
      
      // Add search parameters - Shopify search API format
      // Use 'type' parameter instead of 'resources[type]' for regular search
      const searchQuery = productIds.map((id) => `id:${id}`).join(' OR ');
      url.searchParams.set('q', searchQuery);
      url.searchParams.set('type', 'product');

      this.#activeFetch = new AbortController();

      // Fetch section HTML - Shopify will render it with search.results available
      let sectionHTML;
      try {
        sectionHTML = await sectionRenderer.getSectionHTML(
          sectionId,
          false, // Don't use cache for dynamic content
          url
        );
      } catch (fetchError) {
        console.error('Error fetching section HTML:', fetchError);
        console.error('Error details:', {
          message: fetchError.message,
          stack: fetchError.stack,
          url: url.toString()
        });
        this.style.display = 'none';
        return;
      }

      if (this.#activeFetch.signal.aborted) {
        return;
      }

      if (!sectionHTML || sectionHTML.trim().length === 0) {
        console.error('No section HTML returned from API');
        console.error('URL used:', url.toString());
        console.error('Section ID:', sectionId);
        this.style.display = 'none';
        return;
      }
      
      // Check if HTML contains error messages
      if (sectionHTML.includes('error') || sectionHTML.includes('Error')) {
        console.warn('Section HTML may contain errors');
      }

      // Parse the HTML - try both text/html and application/xml
      const parser = new DOMParser();
      let doc = parser.parseFromString(sectionHTML, 'text/html');
      let sectionElement = doc.querySelector(`recently-viewed-products[id="${this.id}"]`);
      
      // If not found, try parsing as XML/HTML fragment
      if (!sectionElement) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = sectionHTML;
        sectionElement = tempDiv.querySelector(`recently-viewed-products[id="${this.id}"]`) 
          || tempDiv.querySelector('recently-viewed-products');
        doc = tempDiv;
      }
      
      if (!sectionElement) {
        console.warn('Section element not found in HTML');
        this.style.display = 'none';
        return;
      }


      // Look for product list with products - check for resource-list__item children
      let productList = sectionElement.querySelector('.resource-list[data-has-products="true"]');
      
      // If not found, check any resource-list that has children
      if (!productList) {
        const allLists = sectionElement.querySelectorAll('.resource-list');
        for (const list of allLists) {
          if (list.children.length > 0) {
            productList = list;
            break;
          }
        }
      }
      
      // If still not found, get any resource-list
      if (!productList) {
        productList = sectionElement.querySelector('.resource-list');
      }
      
      if (!productList) {
        console.warn('No product list found in section');
        this.style.display = 'none';
        return;
      }
      
      // Check if there are actual product items (resource-list__item)
      const productItems = productList.querySelectorAll('.resource-list__item');
      
      // Also check for product-card elements directly
      const productCards = productList.querySelectorAll('product-card, product-card-link');
      
      // Check if list has any meaningful content
      const hasContent = productItems.length > 0 || productCards.length > 0 || productList.children.length > 0;
      
      if (!hasContent) {
        console.error('Product list is empty - section rendered but no products found');
        console.error('Product list innerHTML:', productList.innerHTML);
        console.error('Section element innerHTML:', sectionElement.innerHTML.substring(0, 2000));
        console.error('This might mean search.results is not populated in the Liquid template');
        this.style.display = 'none';
        return;
      }
      
      // If we have items, update the data attribute
      if (hasContent) {
        productList.setAttribute('data-has-products', 'true');
      }

      // Update data attribute FIRST before replacing content
      this.dataset.hasProducts = 'true';
      
      // Store the section content HTML
      const sectionContentHTML = sectionElement.innerHTML;
      
      // Replace entire section content
      this.innerHTML = sectionContentHTML;
      
      // Wait a moment for DOM to update, then check and fix visibility
      setTimeout(() => {
        // Ensure visibility
        this.style.display = 'block';
        this.style.visibility = 'visible';
        this.style.opacity = '1';
        
        // Update all resource-list elements
        const allLists = this.querySelectorAll('.resource-list');
        
        allLists.forEach((list, index) => {
          if (list.children.length > 0) {
            list.setAttribute('data-has-products', 'true');
            list.style.display = '';
          }
        });
        
        // Check if we have product items
        const finalProductItems = this.querySelectorAll('.resource-list__item');
        
        if (finalProductItems.length === 0) {
          console.error('No product items found after update!');
        } else {
          console.log('Successfully loaded');
        }

        // Initialize Swiper if carousel layout is used
        this.#initSwiper();
      }, 100);
      
    } catch (error) {
      if (this.#activeFetch?.signal.aborted) return;
      console.error('Error loading recently viewed products:', error);
      console.error('Error stack:', error.stack);
      this.style.display = 'none';
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Initialize Swiper carousel if needed
   */
  #initSwiper() {
    if (typeof Swiper === 'undefined') {
      console.warn('Swiper not available, skipping carousel initialization');
      return;
    }

    const sectionId = this.dataset.sectionId;
    const swiperEl = this.querySelector(`#recently-viewed-swiper-${sectionId}`);
    
    if (swiperEl && !swiperEl.swiper) {
      // Get settings from data attributes
      const columns = parseInt(this.dataset.columns || '4', 10);
      const mobileColumns = parseInt(this.dataset.mobileColumns || '2', 10);
      const gap = parseInt(this.dataset.gap || '16', 10);

      const paginationEl = swiperEl.querySelector(`.recently-viewed-pagination-${sectionId}`);
      
      new Swiper(swiperEl, {
        slidesPerView: columns,
        spaceBetween: gap,
        navigation: {
          nextEl: `.recently-viewed-next-${sectionId}`,
          prevEl: `.recently-viewed-prev-${sectionId}`,
        },
        pagination: paginationEl ? {
          el: paginationEl,
          type: 'bullets',
          clickable: true,
          dynamicBullets: false,
          renderBullet: function (index, className) {
            return '<span class="' + className + '"></span>';
          },
        } : false,
        breakpoints: {
          0: {
            slidesPerView: mobileColumns,
            spaceBetween: gap,
          },
          750: {
            slidesPerView: columns,
            spaceBetween: gap,
          }
        },
        loop: false,
        grabCursor: true,
        watchOverflow: false,
        on: {
          init: function() {
            // Force pagination to be visible on mobile
            if (paginationEl && window.innerWidth <= 749) {
              paginationEl.style.display = 'flex';
              paginationEl.style.visibility = 'visible';
              paginationEl.style.opacity = '1';
            }
          }
        }
      });
    }

    // Initialize mobile swiper if exists
    const mobileSwiperEl = this.querySelector(`#recently-viewed-swiper-mobile-${sectionId}`);
    if (mobileSwiperEl && !mobileSwiperEl.swiper) {
      const mobileColumns = parseInt(this.dataset.mobileColumns || '2', 10);
      const gap = parseInt(this.dataset.gap || '16', 10);
      const mobilePaginationEl = mobileSwiperEl.querySelector(`.recently-viewed-pagination-mobile-${sectionId}`);

      new Swiper(mobileSwiperEl, {
        slidesPerView: mobileColumns,
        spaceBetween: gap,
        navigation: {
          nextEl: `.recently-viewed-next-mobile-${sectionId}`,
          prevEl: `.recently-viewed-prev-mobile-${sectionId}`,
        },
        pagination: mobilePaginationEl ? {
          el: mobilePaginationEl,
          type: 'bullets',
          clickable: true,
          dynamicBullets: false,
          renderBullet: function (index, className) {
            return '<span class="' + className + '"></span>';
          },
        } : false,
        loop: false,
        grabCursor: true,
        watchOverflow: false,
        lazy: false,
        preloadImages: true,
        watchSlidesProgress: false,
        on: {
          init: function() {
            // Force pagination to be visible
            if (mobilePaginationEl) {
              mobilePaginationEl.style.display = 'flex';
              mobilePaginationEl.style.visibility = 'visible';
              mobilePaginationEl.style.opacity = '1';
            }
            // Ensure all slides are rendered
            const slides = this.slides;
            if (slides) {
              slides.forEach(slide => {
                if (slide.style) {
                  slide.style.display = '';
                  slide.style.visibility = '';
                }
              });
            }
          }
        }
      });

    }
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}

